// services/prefetchService.ts
/* eslint-disable */
import { api } from "../api";

type PrefetchPriority = "critical" | "high" | "medium" | "low";

interface PrefetchTask {
	key: string;
	fetcher: () => Promise<any>;
	priority: PrefetchPriority;
	ttl: number; // Time to live in ms
	dependencies?: string[];
}

class PrefetchService {
	private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
	private inFlight: Map<string, Promise<any>> = new Map();
	private completedTasks: Set<string> = new Set();

	// Critical APIs that should be loaded during the 10s splash screen
	private criticalTasks: PrefetchTask[] = [
		{
			key: "memberProfile",
			fetcher: () => {
				console.log("[PrefetchService] Calling API: getMemberPortalInitData");
				return api.pages.getMemberPortalInitData(localStorage.getItem("@jwt_group_owner"));
			},
			priority: "critical",
			ttl: 5 * 60 * 1000
		},
		{
			key: "dependentData",
			fetcher: () => {
				console.log("[PrefetchService] Calling API: getDependentPortalInitData");
				return api.pages.getDependentPortalInitData();
			},
			priority: "critical",
			ttl: 5 * 60 * 1000,
			dependencies: ["memberProfile"]
		}
	];
	// High priority APIs - loaded immediately after critical
	private highPriorityTasks: PrefetchTask[] = [
		{
			key: "vebEnrollmentInit",
			fetcher: () => {
				console.log("[PrefetchService] Actually calling VEB API");
				return api.pages.getVEBEnrollmentInitData();
			},
			priority: "high",
			ttl: 3 * 60 * 1000
		}
	];
	// Medium priority - loaded during idle time or on hover
	private mediumPriorityTasks: PrefetchTask[] = [
		// Benefits, documents, etc. can go here later
	];

	async prefetchCritical(): Promise<void> {
		const role = localStorage.getItem("@role");
		const tasks = this.criticalTasks.filter((task) => {
			if (task.key === "dependentData" && role !== "DEPENDENT") return false;
			return true;
		});

		await this.executeTasks(tasks, "critical");
	}

	async prefetchHighPriority(): Promise<void> {
		await this.executeTasks(this.highPriorityTasks, "high");
	}

	async prefetchMediumPriority(): Promise<void> {
		// Use requestIdleCallback if available, otherwise setTimeout
		if ("requestIdleCallback" in window) {
			window.requestIdleCallback(
				() => {
					this.executeTasks(this.mediumPriorityTasks, "medium");
				},
				{ timeout: 2000 }
			);
		} else {
			setTimeout(() => {
				this.executeTasks(this.mediumPriorityTasks, "medium");
			}, 100);
		}
	}

	private async executeTasks(tasks: PrefetchTask[], priority: string): Promise<void> {
		console.log(`[Prefetch] Starting ${priority} priority tasks...`);

		const promises = tasks.map(async (task) => {
			try {
				// Check if already cached and valid
				if (this.isValidCache(task.key)) {
					console.log(`[Prefetch] ${task.key} - served from cache`);
					return;
				}

				// Check if already in flight
				if (this.inFlight.has(task.key)) {
					await this.inFlight.get(task.key);
					return;
				}

				// Execute fetch
				const promise = task
					.fetcher()
					.then((data) => {
						this.cache.set(task.key, {
							data,
							timestamp: Date.now(),
							ttl: task.ttl
						});
						this.inFlight.delete(task.key);
						this.completedTasks.add(task.key);
						console.log(`[Prefetch] ${task.key} - completed`);
						return data;
					})
					.catch((err) => {
						this.inFlight.delete(task.key);
						console.error(`[Prefetch] ${task.key} - failed:`, err);
						throw err;
					});

				this.inFlight.set(task.key, promise);
				await promise;
			} catch (error) {
				// Silent fail for prefetch - don't block UI
				console.warn(`[Prefetch] ${task.key} - silent fail`);
			}
		});

		await Promise.all(promises);
		console.log(`[Prefetch] ${priority} priority tasks completed`);
	}

	// Get cached data or fetch if not available
	async getData<T>(key: string, fetcher?: () => Promise<T>, ttl?: number): Promise<T | null> {
		// Return cached if valid
		if (this.isValidCache(key)) {
			console.log(`[Prefetch] ${key} - returning cached data`);
			return this.cache.get(key)!.data;
		}

		// Return in-flight promise if exists
		if (this.inFlight.has(key)) {
			console.log(`[Prefetch] ${key} - returning in-flight promise`);
			return this.inFlight.get(key);
		}

		// Fetch if fetcher provided
		if (fetcher) {
			console.log(`[Prefetch] ${key} - no cache, fetching fresh`);
			const promise = fetcher().then((data) => {
				this.cache.set(key, {
					data,
					timestamp: Date.now(),
					ttl: ttl || 5 * 60 * 1000
				});
				this.inFlight.delete(key);
				return data;
			});

			this.inFlight.set(key, promise);
			return promise;
		}

		return null;
	}

	// Synchronous check if data is ready (for immediate UI decisions)
	isReady(key: string): boolean {
		return this.completedTasks.has(key) || this.isValidCache(key);
	}

	// Get cached data synchronously (returns null if not cached)
	getCachedData<T>(key: string): T | null {
		if (this.isValidCache(key)) {
			return this.cache.get(key)!.data;
		}
		return null;
	}

	// Prefetch on hover - call this when user hovers over links
	// Add this method to PrefetchService class
	prefetchByRoute(route: string): void {
		const routePrefetchMap: Record<string, string[]> = {
			"/VEB/enrollment": ["vebEnrollmentInit"],
			"/claims": ["claimsSummary", "claimsList"],
			"/benefits": ["benefitsOverview"],
			"/dependents": ["dependentsList"],
			"/documents": ["documentsList"],
			"/health-card": ["healthCardData"],
			"/enrollment": ["enrollmentStatus"],
			"/profile": ["memberProfile"]
		};

		const keysToPrefetch = routePrefetchMap[route];
		if (!keysToPrefetch) {
			console.log(`[Prefetch] No prefetch config for route: ${route}`);
			return;
		}

		console.log(`[Prefetch] Prefetching for route ${route}:`, keysToPrefetch);

		// Find tasks matching these keys
		const allTasks = [...this.criticalTasks, ...this.highPriorityTasks, ...this.mediumPriorityTasks];
		const tasks = allTasks.filter((task) => keysToPrefetch.includes(task.key));

		if (tasks.length === 0) {
			console.log(`[Prefetch] No tasks found for keys:`, keysToPrefetch);
			return;
		}

		if ("requestIdleCallback" in window) {
			window.requestIdleCallback(() => {
				this.executeTasks(tasks, "hover");
			});
		} else {
			setTimeout(() => this.executeTasks(tasks, "hover"), 0);
		}
	}

	// Invalidate cache for specific key
	invalidate(key: string): void {
		this.cache.delete(key);
		this.completedTasks.delete(key);
	}

	// Clear all cache
	clearCache(): void {
		this.cache.clear();
		this.inFlight.clear();
		this.completedTasks.clear();
	}

	private isValidCache(key: string): boolean {
		const cached = this.cache.get(key);
		if (!cached) return false;
		return Date.now() - cached.timestamp < cached.ttl;
	}
}

export const prefetchService = new PrefetchService();
